// Money lock UI — the control, the PIN dialog, and the locked-page screen.
// Engine: src/lib/moneyLock.ts · DB: mig 20260810164741.
//
// Three pieces, because the lock shows up in three shapes:
//   <MoneyLockButton/>  — header control: lock state + the way in and out
//   <MoneyLockedPanel/> — what a 'core' money module renders instead of itself
//   (the third is <Money/> in src/lib/money.tsx, for masked values)

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Lock, Unlock, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { isAdmin } from '@/lib/roles'
import { ValidationError } from '@/lib/errorReporter'
import { withTimeout } from '@/lib/saveTimeout'
import { confirmDialog, alertDialog } from '@/components/Dialogs'
import { useMoneyLock, pinInputProblem } from '@/lib/moneyLock'

function minutesLeft(expiresAt: number): number {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 60_000))
}

// ---------------------------------------------------------------------------
// PIN dialog
// ---------------------------------------------------------------------------
// PIN entry modal — shell + one submit handler; branching is presentation
// (locked-out vs input vs error). Unreachable by the CRAP metric at 0 UI
// coverage; the rules it enforces are tested via pinInputProblem().
// fallow-ignore-next-line complexity
function UnlockDialog({ onClose }: { onClose: () => void }) {
  const { unlock, lockedOutUntil, unlockMinutes } = useMoneyLock()
  const [pin, setPin]         = useState('')
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const inputRef              = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const lockedOut = lockedOutUntil && lockedOutUntil > new Date()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || lockedOut) return
    setBusy(true); setError(null)
    const ok = await unlock(pin)
    setBusy(false)
    if (ok) { onClose(); return }
    setPin('')
    // Deliberately vague: the server won't say whether the PIN was wrong or
    // simply unset, and neither will we.
    setError('That PIN isn’t right.')
    inputRef.current?.focus()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border hairline-strong surface-sunk p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-emerald-500/10 p-2"><Lock size={18} className="text-emerald-400" /></div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-fg">Show money</h2>
            <p className="mt-0.5 text-xs text-fg-muted">
              Rand values are hidden on this tenant. Enter the PIN to show them
              for {unlockMinutes} minutes.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close without showing money"
                  className="tap-target -m-1 rounded-lg p-2 text-fg-muted hover:bg-surface hover:text-fg">
            <X size={16} />
          </button>
        </div>

        {lockedOut ? (
          <p className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300">
            Too many attempts. Try again after{' '}
            {lockedOutUntil.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}.
          </p>
        ) : (
          <>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={e => { setPin(e.target.value); setError(null) }}
              aria-label="PIN"
              placeholder="PIN"
              className="mt-4 w-full rounded-lg border hairline-strong surface px-3 py-2.5
                         text-center text-lg tracking-[0.4em] text-white light:text-fg placeholder:tracking-normal
                         placeholder:text-gray-500 focus:border-emerald-500 focus:outline-none"
            />
            {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
            <button
              type="submit"
              disabled={busy || pin.length < 4}
              className="mt-3 min-h-[44px] w-full rounded-lg bg-emerald-600 px-4 font-medium text-white
                         hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? 'Checking…' : 'Show money'}
            </button>
          </>
        )}
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header control
// ---------------------------------------------------------------------------
// Header control — lock state, countdown tick, dialog toggle. Presentation.
// fallow-ignore-next-line complexity
export function MoneyLockButton({ className }: { className?: string }) {
  const { enabled, locked, expiresAt, lock } = useMoneyLock()
  const [open, setOpen] = useState(false)
  // Re-render each minute so the countdown doesn't sit stale on a page nobody
  // is interacting with.
  const [, tick] = useState(0)
  useEffect(() => {
    if (locked || !expiresAt) return
    const t = setInterval(() => tick(n => n + 1), 30_000)
    return () => clearInterval(t)
  }, [locked, expiresAt])

  if (!enabled) return null

  return (
    <>
      <button
        onClick={() => (locked ? setOpen(true) : lock())}
        title={locked ? 'Rand values are hidden — click to unlock' : 'Click to hide rand values again'}
        className={`tap-target-y flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium
                    ${locked ? 'text-fg-muted hover:bg-surface hover:text-white light:hover:text-fg'
                             : 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'} ${className ?? ''}`}>
        {locked ? <Lock size={14} /> : <Unlock size={14} />}
        <span className="hidden sm:inline">
          {locked ? 'Money hidden' : `Money shown · ${minutesLeft(expiresAt)}m`}
        </span>
      </button>
      {open && <UnlockDialog onClose={() => setOpen(false)} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Locked-page screen — what a 'core' money module renders in place of itself
// ---------------------------------------------------------------------------
export function MoneyLockedPanel({ moduleLabel }: { moduleLabel: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="rounded-2xl surface p-4"><Lock size={28} className="text-fg-muted" /></div>
      <div>
        <h2 className="text-lg font-semibold text-fg">{moduleLabel} is hidden</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-fg-muted">
          This page shows rand values, which are hidden on this tenant. Enter
          the PIN to open it.
        </p>
      </div>
      <button
        onClick={() => setOpen(true)}
        className="min-h-[44px] rounded-lg bg-emerald-600 px-5 font-medium text-white hover:bg-emerald-500">
        Show money
      </button>
      {open && <UnlockDialog onClose={() => setOpen(false)} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Route wrapper for money-only pages that hang off an 'incidental' module
// ---------------------------------------------------------------------------
// The registry flag is per MODULE, but a few ROUTES are money end to end while
// their module must stay open. A payslip is the clearest case: it lives under
// `labour` (which capturers need for Overview/Attendance/Workers), yet the page
// itself is nothing but gross, net, PAYE and UIF. Masking its ~40 values would
// leave a payslip-shaped page of bullets; withholding the page is both cheaper
// and more honest.
//
// Applies to: /payslip/:lineId, /payroll/:periodId/payslips-print,
// /labour/worker/:workerNumber/earnings, and their /farm equivalents.
// NOT to /payslip-share/:token — that is the worker's own payslip, reached by
// a signed link with no tenant session, and has nothing to do with this lock.
export function MoneyOnly({ label, children }: { label: string; children: ReactNode }) {
  const { locked } = useMoneyLock()
  if (locked) return <MoneyLockedPanel moduleLabel={label} />
  return <>{children}</>
}

// ---------------------------------------------------------------------------
// Admin settings card — the only way to switch the lock ON
// ---------------------------------------------------------------------------
// Mounted on the tenant config page for both site kinds. Admin-tier only: the
// PIN reveals every rand in the tenant, so setting it is a tenant-admin act,
// not a labour-manager one (the RPC enforces the same — this gate only keeps
// the form from rendering a button that would 42501).
// Settings form shell — inputs + two handlers. The logic worth testing was
// extracted to pinInputProblem(); what remains is form wiring.
// fallow-ignore-next-line complexity
export function MoneyLockSettingsCard({ folded = false }: { folded?: boolean }) {
  const { data: me } = useCurrentUser()
  const { enabled, known, unlockMinutes } = useMoneyLock()
  const qc = useQueryClient()
  const [pin, setPin]       = useState('')
  const [confirm, setConfirm] = useState('')
  const [mins, setMins]     = useState(String(unlockMinutes))
  const [busy, setBusy]     = useState(false)
  const [error, setError]   = useState<string | null>(null)
  // `folded` (farm hub only): the card sits behind one 44px row. Setting a PIN
  // is a once-per-farm job, so it should not be the first screen of a phone.
  const [open, setOpen]     = useState(!folded)

  if (!isAdmin(me?.role)) return null

  // CRAP 30 == cc 5 at zero coverage, which no async form handler clears. The
  // logic worth testing was extracted to pinInputProblem() and IS covered; what
  // is left is await/try/catch wiring around one RPC.
  // fallow-ignore-next-line complexity
  async function save() {
    setError(null)
    try {
      // Rules live in moneyLock.ts alongside the RPC they mirror — see
      // pinInputProblem(). Keeping them here would let the form and the server
      // drift apart.
      const problem = pinInputProblem(pin, confirm, mins)
      if (problem) throw new ValidationError(problem)
      setBusy(true)
      const { error } = await withTimeout(supabase.rpc('set_money_lock_pin',
        { p_pin: pin.trim(), p_unlock_minutes: Number(mins) }), 'MoneyLock: set_money_lock_pin')
      if (error) throw error
      setPin(''); setConfirm('')
      await qc.invalidateQueries({ queryKey: ['money-lock-state'] })
      await alertDialog({
        title: 'Money lock is on',
        message: 'Rand values are now hidden on this tenant until someone enters the PIN. '
               + 'Write it down somewhere safe — it cannot be read back, only replaced.',
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  async function turnOff() {
    const ok = await confirmDialog({
      title:   'Turn the money lock off?',
      message: 'Every rand value becomes visible to anyone who can open these pages, '
             + 'including whoever is keying data in on this login.',
      confirm: 'Turn it off',
      tone:    'danger',
    })
    if (!ok) return
    setBusy(true)
    const { error } = await withTimeout(supabase.rpc('disable_money_lock'), 'MoneyLock: disable_money_lock')
    setBusy(false)
    if (error) { setError(error.message); return }
    await qc.invalidateQueries({ queryKey: ['money-lock-state'] })
  }

  if (folded && !open) {
    return (
      <button type="button" onClick={() => setOpen(true)} aria-expanded={false}
              className="card flex w-full min-h-[44px] items-center gap-3 text-left hover:bg-raised/60">
        <Lock size={16} className="shrink-0 text-fg-secondary" />
        <span className="flex-1 text-sm font-semibold text-fg">Money lock</span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase
                          ${known && enabled ? 'bg-emerald-500/15 text-emerald-300' : 'surface text-muted'}`}>
          {!known ? 'Checking…' : enabled ? 'On' : 'Off'}
        </span>
        {known && <span className="shrink-0 text-xs text-muted">{enabled ? 'Change' : 'Set up'}</span>}
        <ChevronRight size={18} className="shrink-0 text-muted" />
      </button>
    )
  }

  return (
    <div className="rounded-xl border hairline surface-sunk p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg surface p-2"><Lock size={16} className="text-fg-secondary" /></div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-fg">Money lock</h3>
          <p className="mt-0.5 text-xs text-fg-muted">
            Hides every rand value on this tenant — wages, costs, invoices —
            until someone enters a PIN. For sites where people key data in on a
            shared login.
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase
                          ${enabled ? 'bg-emerald-500/15 text-emerald-300' : 'surface text-fg-muted'}`}>
          {enabled ? 'On' : 'Off'}
        </span>
      </div>

      {/* Said plainly, on the screen where it's switched on — not buried in a
          doc. Someone enabling this should know what it does and doesn't do
          before they rely on it. */}
      <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200/90">
        This hides values on screen. It is not a security boundary — the figures
        are still sent to the browser, so someone technical on this login could
        still reach them. The proper fix is giving each person their own login.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input type="password" value={pin} onChange={e => setPin(e.target.value)}
               aria-label={enabled ? 'New PIN' : 'PIN'}
               placeholder={enabled ? 'New PIN' : 'PIN'} autoComplete="new-password"
               className="rounded-lg border hairline-strong surface px-3 py-2 text-sm text-white light:text-fg
                          placeholder:text-gray-500 focus:border-emerald-500 focus:outline-none" />
        <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
               aria-label="Confirm PIN"
               placeholder="Confirm PIN" autoComplete="new-password"
               className="rounded-lg border hairline-strong surface px-3 py-2 text-sm text-white light:text-fg
                          placeholder:text-gray-500 focus:border-emerald-500 focus:outline-none" />
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-fg-muted">
        Stay unlocked for
        <input type="number" min={1} max={240} value={mins} onChange={e => setMins(e.target.value)}
               className="w-20 rounded-lg border hairline-strong surface px-2 py-1 text-right
                          text-sm text-white light:text-fg focus:border-emerald-500 focus:outline-none" />
        minutes
      </label>

      {error && <p className="mt-2 text-xs text-red-300">{error}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={save} disabled={busy}
                className="min-h-[44px] rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white
                           hover:bg-emerald-500 disabled:opacity-40">
          {busy ? 'Saving…' : enabled ? 'Change PIN' : 'Turn on money lock'}
        </button>
        {enabled && (
          <button onClick={turnOff} disabled={busy}
                  className="min-h-[44px] rounded-lg border hairline-strong px-4 text-sm text-gray-300 light:text-fg-secondary
                             hover:bg-gray-800 light:hover:bg-raised disabled:opacity-40">
            Turn off
          </button>
        )}
      </div>
    </div>
  )
}
